import type { WebPushPublicKey } from "@spare-cart/contracts/notifications";
import { apiClient } from "@core/api";

export const isWebPushSupported = (): boolean =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

const urlBase64ToUint8Array = (base64Url: string): Uint8Array<ArrayBuffer> => {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const subscriptionKeys = (subscription: PushSubscription): { p256dh: string; auth: string } => {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (p256dh === undefined || auth === undefined) {
    throw new Error("Push subscription is missing its encryption keys");
  }
  return { p256dh, auth };
};

export const getExistingWebPushSubscription = async (): Promise<PushSubscription | null> => {
  if (!isWebPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
};

export const subscribeWebPush = async (): Promise<boolean> => {
  if (!isWebPushSupported()) return false;

  const { publicKey } = await apiClient.get<WebPushPublicKey>("/web-push/public-key");
  if (publicKey === null) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const registration = await navigator.serviceWorker.register("/sw.js");
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const { p256dh, auth } = subscriptionKeys(subscription);
  await apiClient.post("/web-push/subscribe", {
    endpoint: subscription.endpoint,
    keys: { p256dh, auth },
    userAgent: navigator.userAgent,
  });
  return true;
};

export const unsubscribeWebPush = async (): Promise<void> => {
  const subscription = await getExistingWebPushSubscription();
  if (subscription === null) return;

  try {
    await apiClient.remove("/web-push/subscribe", { endpoint: subscription.endpoint });
  } catch {
    // best-effort: still drop the local subscription even if the server call fails
  }
  await subscription.unsubscribe();
};
